import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Benefit/coverage breakup that hangs off a parent Premium Rule.
 * Sent on Insert/Update. All benefit amounts are nullable decimals.
 * NOTE the casing: the API serializes ICU hospitalization as `icUhospitalization`.
 */
export interface PremiumRuleDetailDto {
  id?: number;
  premiumRulesID: number; // parent rule id — carried, never typed
  masterPolicyId: number; // parent rule's policy — carried, never typed
  criticalIllness?: number | null;
  dailyHospitalisation?: number | null;
  icUhospitalization?: number | null; // matches server casing exactly
  accidentalDeath?: number | null;
  permanentTotalDisablement?: number | null;
  permanentPartialDisablement?: number | null;
  convalescenceCover?: number | null;
  doctorConsultant?: number | null;
  otherType?: string | null; // required when `other` > 0
  other?: number | null;
  isActive: boolean;
}

/** Shape returned on list/detail (joined premiumCode + audit fields). */
export interface PremiumRuleDetail extends PremiumRuleDetailDto {
  id: number;
  premiumCode?: string | null;
  isDeleted?: boolean;
  dtEntry?: string | null;
  entryBy?: number | null;
}

/** Normalize a possibly PascalCase row to the camelCase model. */
function mapDetail(x: any): PremiumRuleDetail {
  return {
    id: x.Id ?? x.id,
    premiumRulesID: x.PremiumRulesID ?? x.premiumRulesID,
    masterPolicyId: x.MasterPolicyId ?? x.masterPolicyId,
    premiumCode: x.PremiumCode ?? x.premiumCode ?? null,
    criticalIllness: x.CriticalIllness ?? x.criticalIllness ?? null,
    dailyHospitalisation: x.DailyHospitalisation ?? x.dailyHospitalisation ?? null,
    icUhospitalization: x.ICUhospitalization ?? x.icUhospitalization ?? null,
    accidentalDeath: x.AccidentalDeath ?? x.accidentalDeath ?? null,
    permanentTotalDisablement: x.PermanentTotalDisablement ?? x.permanentTotalDisablement ?? null,
    permanentPartialDisablement: x.PermanentPartialDisablement ?? x.permanentPartialDisablement ?? null,
    convalescenceCover: x.ConvalescenceCover ?? x.convalescenceCover ?? null,
    doctorConsultant: x.DoctorConsultant ?? x.doctorConsultant ?? null,
    otherType: x.OtherType ?? x.otherType ?? null,
    other: x.Other ?? x.other ?? null,
    isActive: x.IsActive ?? x.isActive ?? true,
    isDeleted: x.IsDeleted ?? x.isDeleted ?? false,
    dtEntry: x.DtEntry ?? x.dtEntry ?? null,
    entryBy: x.EntryBy ?? x.entryBy ?? null,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapDetail) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapDetail(res.data) };
  if (Array.isArray(res)) return res.map(mapDetail);
  return res;
}

@Injectable({ providedIn: 'root' })
export class PremiumRuleDetailService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/PremiumRuleDetail`;

  getByPremiumRule(premiumRulesId: number) {
    return this.http
      .get(`${this.baseUrl}/GetByPremiumRule`, { params: { premiumRulesId } })
      .pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  insert(dto: PremiumRuleDetailDto) {
    return this.http.post(`${this.baseUrl}/Insert`, dto);
  }

  update(dto: PremiumRuleDetailDto) {
    return this.http.post(`${this.baseUrl}/Update`, dto);
  }

  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
